import React, {useEffect, useRef} from 'react'
import {ReactComponent as PlusIcon} from '../../../../../../assets/icons/plus.svg'
import {ReactComponent as CloseCircleIcon} from '../../../../../../assets/icons/close-circle.svg'
import {toaster} from '../../../../../../shared/PFToast'


const DEFAULT_MAX_PROMPT_LENGTH = 500
const DEFAULT_MIN_PROMPT_LENGTH = 20
const MAX_FILE_SIZE = 7 * 1024 * 1024 // 7MB in bytes

const ImagePromptInput = ({
    className = '', 
    value, 
    onChangeText, 
    images = [], 
    onChangeImages, 
    onFileSizeError, 
    disabled = false,
    maxPromptLength = DEFAULT_MAX_PROMPT_LENGTH,
    minPromptLength = DEFAULT_MIN_PROMPT_LENGTH,
    accept,
}) => {
    const fileInputRef = useRef(null)
    const textRef = useRef(null)

    const handlePickImage = () => {
        if (fileInputRef.current) fileInputRef.current.click()
    }

    const handleFileChange = (e) => {
        const fileList = e.target.files
        if (!fileList || fileList.length === 0) return
        const file = fileList[0]
        
        // Get accept value (default or from prop)
        const acceptValue = accept || 'image/png, image/jpeg, image/jpg'
        
        // Parse accept MIME types
        const acceptMimeTypes = acceptValue.split(',').map(mime => mime.trim().toLowerCase())
        
        // Map MIME types to allowed extensions
        const mimeToExtensions = {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg', '.jfif', '.pjpeg', '.pjp'],
            'image/jpg': ['.jpg', '.jpeg', '.jfif', '.pjpeg', '.pjp'],
        }
        
        // Get all allowed extensions from accept MIME types
        const allowedExtensions = []
        acceptMimeTypes.forEach(mimeType => {
            if (mimeToExtensions[mimeType]) {
                allowedExtensions.push(...mimeToExtensions[mimeType])
            }
        })
        
        // Validate file MIME type
        const fileMimeType = (file.type || '').toLowerCase()
        const isMimeTypeValid = acceptMimeTypes.some(acceptedMime => fileMimeType === acceptedMime)
        
        // Validate file extension
        const lastDotIndex = file.name.lastIndexOf('.')
        const fileExtension = lastDotIndex >= 0 ? file.name.substring(lastDotIndex).toLowerCase() : ''
        const isExtensionValid = fileExtension && allowedExtensions.includes(fileExtension)
        
        // Block file if neither MIME type nor extension is valid
        if (!isMimeTypeValid && !isExtensionValid) {
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
            // Show error message
            toaster({
                type: 'error',
                message: 'Only accepts files with extension: .png, .jfif, .pjpeg, .pjp, .jpg',
                placement: 'top',
            })
            return
        }
        
        // Clear error when re-uploading image
        onFileSizeError && onFileSizeError(false)
        
        const newItem = {
            id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
            file,
            previewUrl: URL.createObjectURL(file),
            fullWidth: false,
        }
        // accept only one image: replace existing
        onChangeImages && onChangeImages([newItem])
    }

    const handleRemoveImage = (id) => {
        const next = (images || []).filter((it) => it.id !== id)
        onChangeImages && onChangeImages(next)
        // Clear error when image is removed
        onFileSizeError && onFileSizeError(false)
    }

    const toggleFullWidth = (id) => {
        const next = (images || []).map((it) => (it.id === id ? {...it, fullWidth: !it.fullWidth} : it))
        onChangeImages && onChangeImages(next)
    }

    const placeCaretAtEnd = (el) => {
        if (!el) return
        const range = document.createRange()
        range.selectNodeContents(el)
        range.collapse(false)
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
    }

    const handlePaste = (e) => {
        e.preventDefault()
        
        // Get plain text from clipboard
        const text = (e.clipboardData || window.clipboardData).getData('text/plain')
        
        if (!text) return
        
        const el = textRef.current
        if (!el) return
        
        // Get current selection
        const selection = window.getSelection()
        if (!selection.rangeCount) return
        
        const range = selection.getRangeAt(0)
        
        // Delete selected content if any
        range.deleteContents()
        
        // Create text node with plain text (no formatting)
        const textNode = document.createTextNode(text)
        range.insertNode(textNode)
        
        // Move cursor after inserted text
        range.setStartAfter(textNode)
        range.collapse(true)
        selection.removeAllRanges()
        selection.addRange(range)
        
        // Get updated text content and enforce max length
        let currentText = el.textContent || ''
        if (currentText.length > maxPromptLength) {
            currentText = currentText.substring(0, maxPromptLength)
            el.textContent = currentText
            placeCaretAtEnd(el)
        }
        
        // Trigger onChangeText callback
        onChangeText && onChangeText(currentText)
    }

    // Sync external value to the editable div when user is not typing in it
    useEffect(() => {
        const el = textRef.current
        if (!el) return
        const isFocused = document.activeElement === el
        const current = el.textContent || ''
        const incoming = value || ''
        if (!isFocused && current !== incoming) {
            el.textContent = incoming
            placeCaretAtEnd(el)
        }
    }, [value])

    return (
        <div className={`PromptComposer ${className}`}>
            <div className="ComposerInput">
                {images && images.length > 0 && (
                    <div className="ComposerAttachmentGrid">
                        {images.map((img) => (
                            <div
                                key={img.id}
                                className={`ComposerAttachment ${img.fullWidth ? 'full' : ''}`}
                                onClick={() => toggleFullWidth(img.id)}
                            >
                                <img src={img.previewUrl} alt="attachment" />
                                <button
                                    type="button"
                                    aria-label="Remove image"
                                    className="AttachmentRemove"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleRemoveImage(img.id)
                                    }}
                                >
                                    <CloseCircleIcon />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <div
                    ref={textRef}
                    className="ComposerTextInput"
                    contentEditable
                    role="textbox"
                    aria-multiline="true"
                    data-placeholder="Describe the background you want"
                    onPaste={handlePaste}
                    onInput={(e) => {
                        let text = e.currentTarget.textContent || ''
                        // Enforce max length
                        if (text.length > maxPromptLength) {
                            text = text.substring(0, maxPromptLength)
                            e.currentTarget.textContent = text
                            placeCaretAtEnd(e.currentTarget)
                        }
                        onChangeText && onChangeText(text)
                        if (text.trim() === '') {
                            e.currentTarget.innerHTML = ''
                        }
                    }}
                    onBlur={(e) => {
                        const text = e.currentTarget.textContent || ''
                        if (text.trim() === '') {
                            e.currentTarget.innerHTML = ''
                        }
                    }}
                    suppressContentEditableWarning
                />
                <div className="ComposerActions">
                    <button
                        id={'prompt-input-add-image-btn'}
                        type={'button'}
                        className={'p-0 btn btn-link text-decoration-none text-primary d-flex align-items-center gap-2'}
                        onClick={handlePickImage}
                        disabled={disabled || (images && images.length > 0)}
                        style={{
                            opacity: disabled || (images && images.length > 0) ? 0.5 : 1,
                            cursor: disabled || (images && images.length > 0) ? 'not-allowed' : 'pointer',
                            pointerEvents: disabled || (images && images.length > 0) ? 'none' : 'auto'
                        }}
                    >
                        <PlusIcon className="PlusIcon" />
                        Add image
                    </button>
                    <span className={`ms-auto ${(value || '').length > maxPromptLength ? 'text-danger' : (value || '').length < minPromptLength ? 'text-warning' : 'text-muted'}`}>
                        {(value || '').length}/{maxPromptLength}
                        {(value || '').length > 0 && (value || '').length < minPromptLength && (
                            <span className="ms-1">(min {minPromptLength})</span>
                        )}
                    </span>
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept || 'image/png, image/jpeg, image/jpg'}
                    style={{display: 'none'}}
                    onChange={handleFileChange}
                />
            </div>
        </div>
    )
}

export default ImagePromptInput

